/**
 * Dashboard Flow — 实时 Worker 日志 + Session 交易决策面板
 *
 * 布局：全屏（无 TabBar），左 60% 日志流 + 右 40% 决策卡片流
 * Auth：页面内检查 localStorage token，无 token → /login
 *        后端 userId=2 检查 → SSE 403 → 显示 "Access Denied"
 *
 * SSE 生命周期：
 *   1. connect → history replay (log + alpha + batch-done)
 *   2. batch-done → 进入实时模式，顶部状态栏显示 "实时监控中"
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { Navigate } from "react-router-dom";
import {
  connectDashboardFlow,
  type DashboardEvent,
  type DashboardLogEvent,
  type DashboardAlphaEvent,
} from "@/api/dashboard";

// ── 常量 ──────────────────────────────────────────────────────────────

const MAX_LOGS = 500;
const MAX_DECISIONS = 200;

// ── Alpha 事件类型可读名称 ────────────────────────────────────────────

const alphaTypeLabel: Record<string, string> = {
  view: "分析师观点",
  round: "辩论决策",
  card: "口播卡片",
  stage: "阶段切换",
  context: "市场上下文",
  bull_bear: "牛熊辩论",
  risk_stance: "风险评估",
  risk_summary: "风险汇总",
  trace: "LLM 追踪",
  pre_trade: "预交易信号",
};

// ── 主组件 ────────────────────────────────────────────────────────────

export default function DashboardFlowPage() {
  // Auth guard
  const token =
    localStorage.getItem("alpha_token") ||
    localStorage.getItem("token");

  // State
  const [logs, setLogs] = useState<DashboardLogEvent[]>([]);
  const [decisions, setDecisions] = useState<DashboardAlphaEvent[]>([]);
  const [connected, setConnected] = useState(false);
  const [paused, setPaused] = useState(false);
  const [logFilter, setLogFilter] = useState<"all" | "info" | "error">("all");
  const [accessDenied, setAccessDenied] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [historyDone, setHistoryDone] = useState(false);

  // Refs
  const logEndRef = useRef<HTMLDivElement>(null);
  const decisionEndRef = useRef<HTMLDivElement>(null);
  const autoScrollLog = useRef(true);
  const autoScrollDecision = useRef(true);
  const pausedRef = useRef(false);

  // Keep pausedRef in sync
  useEffect(() => { pausedRef.current = paused; }, [paused]);

  // ── SSE 连接 ──────────────────────────────────────────────────────

  useEffect(() => {
    if (!token) return;
    const eventBuffer: DashboardEvent[] = [];
    let flushTimer: ReturnType<typeof setInterval>;

    const es = connectDashboardFlow({
      onEvent: (event) => {
        eventBuffer.push(event);
      },
      onConnected: () => setConnected(true),
      onDisconnected: () => setConnected(false),
      onAccessDenied: () => setAccessDenied(true),
    });

    // Batch-flush every 200ms for smooth rendering
    flushTimer = setInterval(() => {
      if (eventBuffer.length === 0) return;
      const batch = eventBuffer.splice(0);
      if (pausedRef.current) return;

      // Check for batch-done in the batch
      const hasBatchDone = batch.some((e) => e.type === "batch-done");
      if (hasBatchDone) setHistoryDone(true);

      setLogs((prev) => {
        const newLogs = batch.filter(
          (e): e is DashboardLogEvent => e.type === "log"
        );
        if (newLogs.length === 0) return prev;
        const combined = [...prev, ...newLogs];
        return combined.slice(-MAX_LOGS);
      });

      setDecisions((prev) => {
        const newDecisions = batch.filter(
          (e): e is DashboardAlphaEvent => e.type === "alpha"
        );
        if (newDecisions.length === 0) return prev;
        const combined = [...prev, ...newDecisions];
        return combined.slice(-MAX_DECISIONS);
      });
    }, 200);

    return () => {
      es.close();
      clearInterval(flushTimer);
    };
  }, [token]);

  // ── 自动滚动 ──────────────────────────────────────────────────────

  useEffect(() => {
    if (autoScrollLog.current && logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [logs]);

  useEffect(() => {
    if (autoScrollDecision.current && decisionEndRef.current) {
      decisionEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [decisions]);

  const handleLogScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    autoScrollLog.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  }, []);

  const handleDecisionScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      autoScrollDecision.current =
        el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    },
    []
  );

  // ── 过滤后的日志 ──────────────────────────────────────────────────

  const filteredLogs =
    logFilter === "all"
      ? logs
      : logs.filter((l) => l.level === logFilter);

  // ── 计数 ──────────────────────────────────────────────────────────

  const errorCount = logs.filter((l) => l.level === "error").length;
  const sessionIds = new Set(
    decisions.map((d) => d.sessionId).filter(Boolean)
  );

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  // ── Access Denied 状态 ────────────────────────────────────────────

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-background-950 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-shield-user-line text-6xl text-red-400 mb-4 block" />
          <h1 className="text-foreground-50 text-2xl font-bold mb-2">
            暂无查看权限
          </h1>
          <p className="text-foreground-400">
            该实时资金流页面仅向指定操作员账号开放。
          </p>
          <a
            href="/"
            className="text-primary-400 mt-4 inline-block hover:underline"
          >
            返回首页
          </a>
        </div>
      </div>
    );
  }

  // ── 主 UI ─────────────────────────────────────────────────────────

  return (
    <div className="h-screen flex flex-col bg-background-950 text-foreground-200">
      {/* ── 顶部栏 ───────────────────────────────────────────────── */}
      <header className="flex-shrink-0 h-12 border-b border-background-800 flex items-center px-4 gap-4 bg-background-900">
        <h1 className="font-bold text-foreground-50 text-sm tracking-wide">
          策略实时资金流
        </h1>

        {/* 连接状态 */}
        <span
          className={`inline-flex items-center gap-1 text-xs ${
            connected ? "text-accent-400" : "text-red-400"
          }`}
        >
          <span
            className={`w-2 h-2 rounded-full ${
              connected
                ? "bg-accent-400 animate-pulse"
                : "bg-red-400"
            }`}
          />
          {connected
            ? historyDone
              ? "实时监控中"
              : "加载历史..."
            : "断开"}
        </span>

        {/* 统计 */}
        <span className="text-xs text-foreground-500">
          日志{" "}
          <span className="text-foreground-300">{logs.length}</span>
          {" · "}
          错误 <span className="text-red-400">{errorCount}</span>
          {" · "}
          决策{" "}
          <span className="text-foreground-300">{decisions.length}</span>
          {" · "}
          Sessions{" "}
          <span className="text-foreground-300">{sessionIds.size}</span>
        </span>

        <div className="flex-1" />

        {/* 日志过滤 */}
        <div className="flex rounded bg-background-800 overflow-hidden">
          {(["all", "info", "error"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setLogFilter(f)}
              className={`px-3 py-1 text-xs ${
                logFilter === f
                  ? "bg-primary-600 text-white"
                  : "text-foreground-400 hover:text-foreground-50"
              }`}
            >
              {f === "all" ? "全部" : f}
            </button>
          ))}
        </div>

        {/* 暂停 */}
        <button
          onClick={() => setPaused((p) => !p)}
          className={`px-3 py-1 text-xs rounded ${
            paused
              ? "bg-amber-600 text-white"
              : "bg-background-800 text-foreground-400 hover:text-foreground-50"
          }`}
        >
          <i
            className={`${paused ? "ri-play-line" : "ri-pause-line"} mr-1`}
          />
          {paused ? "已暂停" : "暂停"}
        </button>
      </header>

      {/* ── 主体双栏 ─────────────────────────────────────────────── */}
      <div className="flex-1 flex overflow-hidden">
        {/* 左侧：日志流 */}
        <div className="w-[60%] border-r border-background-800 flex flex-col">
          <div
            className="flex-1 overflow-y-auto font-mono text-xs leading-relaxed p-1"
            onScroll={handleLogScroll}
          >
            {filteredLogs.map((log, i) => {
              const isError = log.level === "error";
              const time = new Date(log.timestamp).toLocaleTimeString(
                "zh-CN",
                { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" }
              );
              return (
                <div
                  key={i}
                  className={`flex gap-2 px-2 py-0.5 hover:bg-background-900/50 ${
                    isError ? "bg-red-950/30" : ""
                  }`}
                >
                  <span className="text-foreground-600 flex-shrink-0 w-[72px]">
                    {time}
                  </span>
                  <span
                    className={`flex-shrink-0 w-[44px] font-medium ${
                      isError ? "text-red-400" : "text-accent-400"
                    }`}
                  >
                    {log.level.toUpperCase()}
                  </span>
                  <span
                    className={`break-all ${
                      isError ? "text-red-300" : "text-accent-300"
                    }`}
                  >
                    {log.message}
                  </span>
                </div>
              );
            })}
            <div ref={logEndRef} />
          </div>
        </div>

        {/* 右侧：决策卡片流 */}
        <div className="w-[40%] flex flex-col">
          <div
            className="flex-1 overflow-y-auto p-2 space-y-2"
            onScroll={handleDecisionScroll}
          >
            {decisions.map((d, i) => {
              const a = d.alpha;
              const label = alphaTypeLabel[a.type] || a.type;
              const time = new Date(d.timestamp).toLocaleTimeString(
                "zh-CN",
                { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" }
              );
              const isExpanded = expandedId === i;
              const payload = a.payload as Record<string, unknown> | undefined;

              return (
                <div
                  key={i}
                  className="bg-background-900 border border-background-800 rounded-card p-3 text-xs cursor-pointer hover:border-background-700 transition-colors"
                  onClick={() => setExpandedId(isExpanded ? null : i)}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-primary-400 font-medium">
                      Session #{d.sessionId}
                    </span>
                    <span className="text-foreground-600">{time}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-block px-1.5 py-0.5 rounded bg-background-800 text-foreground-300 text-[11px]">
                      {label}
                    </span>
                    {a.type === "round" && payload?.finalAction && (
                      <span className="text-gold-400">
                        {String(payload.ticker)} →{" "}
                        {String(payload.finalAction)} 🎯
                        {String(payload.confidence)}/5
                      </span>
                    )}
                    {a.type === "card" && payload?.action && (
                      <span className="text-accent-400">
                        {String(payload.action)}{" "}
                        {String(payload.target)}
                      </span>
                    )}
                    {a.type === "view" && payload?.agentType && (
                      <span className="text-purple-400">
                        {String(payload.agentType)}:{" "}
                        {String(payload.verdict)}
                      </span>
                    )}
                  </div>

                  {/* 展开 JSON */}
                  {isExpanded && (
                    <pre className="mt-2 p-2 bg-background-950 rounded text-foreground-500 overflow-x-auto max-h-[300px] overflow-y-auto text-[10px] leading-relaxed border border-background-800">
                      {JSON.stringify(a, null, 2)}
                    </pre>
                  )}
                </div>
              );
            })}
            <div ref={decisionEndRef} />
          </div>
        </div>
      </div>
    </div>
  );
}
